import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/components/ThemeProvider';
import { useState, useEffect } from 'react';
import { 
  LayoutDashboard, 
  FolderKanban, 
  Settings, 
  LogOut,
  Plus,
  Sparkles,
  Wand2,
  Zap,
  Link2,
  CreditCard,
  Crown,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Compass,
  Moon,
  Sun,
  Shield,
  Mail,
  Users,
  Receipt,
  HeadphonesIcon,
  Settings2,
  BookOpen,
  X,
  Monitor
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const Sidebar = ({ isOpen, onClose }) => {
  const { user, logout, api } = useAuth();
  const { theme, toggleTheme, isForcedDark, isUsingSystem, useSystemTheme, setTheme, isDark } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [billingStatus, setBillingStatus] = useState(null);
  const [usageSummary, setUsageSummary] = useState(null);
  const [showUsageDetails, setShowUsageDetails] = useState(false);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    if (onClose) {
      onClose();
    }
  }, [location.pathname]);

  // Load billing status and usage
  useEffect(() => {
    const loadBillingData = async () => {
      try {
        const [statusRes, usageRes] = await Promise.all([
          api.get('/billing/status'),
          api.get('/billing/usage-summary')
        ]);
        setBillingStatus(statusRes.data);
        setUsageSummary(usageRes.data);
      } catch (error) {
        // Silent fail
      }
    };
    
    if (api) {
      loadBillingData();
      const interval = setInterval(loadBillingData, 60000);
      return () => clearInterval(interval);
    }
  }, [api]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/funnel/new', icon: Plus, label: 'Nuevo Funnel', highlight: true },
    { to: '/proyectos', icon: FolderKanban, label: 'Mis Proyectos' },
    { to: '/billing', icon: CreditCard, label: 'Facturación' },
    { to: '/soporte', icon: HeadphonesIcon, label: 'Soporte' },
    { to: '/tutoriales', icon: BookOpen, label: 'Tutoriales' },
    { to: '/configuracion', icon: Settings, label: 'Ajustes' },
  ];

  const adminItems = [
    { to: '/admin/usuarios', icon: Users, label: 'Usuarios' },
    { to: '/admin/suscripciones', icon: CreditCard, label: 'Suscripciones' },
    { to: '/admin/transacciones', icon: Receipt, label: 'Transacciones' },
    { to: '/admin/soporte', icon: HeadphonesIcon, label: 'Soporte' },
    { to: '/admin/tutoriales', icon: BookOpen, label: 'Tutoriales' },
    { to: '/admin/sistema', icon: Settings2, label: 'Sistema' },
    { to: '/admin/emails', icon: Mail, label: 'Email Marketing' },
    { to: '/admin/prompts', icon: Wand2, label: 'Prompts Maestros' },
    { to: '/admin/ai-config', icon: Zap, label: 'Configuración IA' },
    { to: '/admin/api-config', icon: Link2, label: 'APIs Externas' },
  ];

  return (
    <aside 
      className={`
        w-64 border-r border-periwinkle-300/30 dark:border-white/10 
        bg-white/95 dark:bg-[#0A0A12]/95 backdrop-blur-xl 
        flex flex-col h-screen fixed left-0 top-0 
        shadow-glass-light dark:shadow-glass-dark
        transition-transform duration-300 ease-in-out z-50
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}
      data-testid="sidebar"
    >
      {/* Mobile Close Button */}
      <button
        onClick={onClose}
        className="lg:hidden absolute top-4 right-4 p-2 rounded-lg hover:bg-periwinkle-100/50 dark:hover:bg-white/10 transition-colors"
        data-testid="sidebar-close-btn"
      >
        <X className="w-5 h-5 text-foreground/60" />
      </button>

      {/* Logo */}
      <div className="p-6 border-b border-periwinkle-300/30 dark:border-white/10">
        <div className="flex items-center gap-3">
          <img 
            src="/logo-dark.png"
            alt="FASCINAI ONE" 
            className={`h-10 object-contain transition-all duration-200 ${
              theme === 'dark' ? '' : 'brightness-[0.15] saturate-150'
            }`}
          />
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 overflow-y-auto">
        {/* Strategy Advisor - Special Item */}
        <NavLink
          to="/strategy-advisor"
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-3 rounded-xl mb-1 transition-all duration-200 ${
              isActive
                ? 'bg-gradient-to-r from-amber-500/20 to-orange-500/10 text-amber-600 shadow-sm'
                : 'text-foreground/70 hover:bg-periwinkle-100/50 hover:text-amber-600'
            }`
          }
          data-testid="nav-strategy-advisor"
        >
          <Compass className="w-5 h-5" />
          <span className="font-medium">¿Qué estrategia elegir?</span>
          <span className="ml-auto px-2 py-0.5 text-[10px] font-semibold bg-brand-500 text-white rounded-full">
            Nuevo
          </span>
        </NavLink>
        
        {/* Separator */}
        <div className="my-3 mx-4 border-t border-periwinkle-300/30" />
        
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 rounded-xl mb-1 transition-all duration-200 ${
                isActive
                  ? 'bg-brand-500/10 text-brand-500 shadow-sm font-semibold'
                  : item.highlight
                  ? 'text-brand-500 hover:bg-brand-500/5'
                  : 'text-foreground/70 hover:bg-periwinkle-100/50 hover:text-foreground'
              }`
            }
            data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
          >
            <item.icon className="w-5 h-5" />
            <span className="font-medium">{item.label}</span>
          </NavLink>
        ))}

        {/* Admin Section */}
        {user?.role === 'super_admin' && (
          <>
            <div className="my-3 px-4">
              <div className="border-t border-periwinkle-300/30" />
              <span className="block mt-3 text-xs text-foreground/40 uppercase tracking-wider font-semibold">
                Administración
              </span>
            </div>
            {adminItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-xl mb-1 transition-all duration-200 ${
                    isActive
                      ? 'bg-lavender/20 text-lavender-dark shadow-sm'
                      : 'text-foreground/70 hover:bg-lavender/10 hover:text-lavender-dark'
                  }`
                }
                data-testid={`nav-admin-${item.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </NavLink>
            ))}
          </>
        )}
      </nav>

      {/* Low Credits Alerts */}
      <div className="mx-3 mb-2">
        {/* Low Credits Alert */}
        {billingStatus?.credits_balance <= 100 && billingStatus?.credits_balance > 20 && (
          <div className="p-2.5 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-[11px] text-amber-700 dark:text-amber-400 font-medium">
                  Créditos bajos ({billingStatus.credits_balance})
                </p>
                <NavLink to="/billing" className="text-[10px] text-amber-600 dark:text-amber-500 hover:underline">
                  Comprar más →
                </NavLink>
              </div>
            </div>
          </div>
        )}
        
        {/* Critical Credits Alert */}
        {billingStatus?.credits_balance <= 20 && billingStatus?.credits_balance >= 0 && (
          <div className="p-2.5 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30">
            <div className="flex items-start gap-2">
              <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse mt-1 flex-shrink-0" />
              <div>
                <p className="text-[11px] text-red-700 dark:text-red-400 font-semibold">
                  ¡{billingStatus.credits_balance} créditos!
                </p>
                <NavLink 
                  to="/billing" 
                  className="text-[10px] text-red-600 dark:text-red-400 hover:underline"
                >
                  Comprar ahora →
                </NavLink>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* User Info & Theme Toggle & Logout */}
      <div className="p-3 border-t border-periwinkle-300/30 dark:border-white/10">
        {/* Credits - Compact inline version */}
        <div className="flex items-center justify-between mb-3 px-1">
          <div className="flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-brand-500" />
            <span className="text-xs text-foreground/60">Créditos:</span>
            <span className="text-sm font-bold text-foreground">
              {billingStatus?.credits_balance?.toLocaleString() || '0'}
            </span>
          </div>
          <NavLink 
            to="/billing" 
            className="text-[10px] text-brand-500 hover:underline font-medium"
          >
            + Comprar
          </NavLink>
        </div>
        
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-brand-500 to-lavender flex items-center justify-center shadow-sm flex-shrink-0">
            <span className="text-white font-semibold text-sm">
              {user?.name?.charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-foreground truncate">{user?.name}</p>
            <p className="text-xs text-foreground/50 truncate">{user?.agency_name}</p>
          </div>
        </div>
        
        {/* Theme Toggle - 3 Options - Compact */}
        <div className="flex items-center gap-0.5 mb-2 p-0.5 bg-periwinkle-100/30 dark:bg-white/5 rounded-lg">
          <Button
            variant="ghost"
            onClick={() => setTheme('light')}
            disabled={isForcedDark}
            className={`flex-1 justify-center gap-1 rounded-md py-1.5 px-2 h-auto text-[11px] transition-all duration-200 ${
              !isUsingSystem && theme === 'light' && !isForcedDark
                ? 'bg-white dark:bg-gray-800 shadow-sm text-foreground' 
                : 'text-foreground/50 hover:text-foreground/70'
            }`}
            data-testid="theme-light"
            title="Modo claro"
          >
            <Sun className="w-3.5 h-3.5" />
            <span>Claro</span>
          </Button>
          
          <Button
            variant="ghost"
            onClick={useSystemTheme}
            disabled={isForcedDark}
            className={`flex-1 justify-center gap-1 rounded-md py-1.5 px-2 h-auto text-[11px] transition-all duration-200 ${
              isUsingSystem && !isForcedDark
                ? 'bg-white dark:bg-gray-800 shadow-sm text-foreground' 
                : 'text-foreground/50 hover:text-foreground/70'
            }`}
            data-testid="theme-system"
            title="Automático según sistema"
          >
            <Monitor className="w-3.5 h-3.5" />
            <span>Auto</span>
          </Button>
          
          <Button
            variant="ghost"
            onClick={() => setTheme('dark')}
            disabled={isForcedDark}
            className={`flex-1 justify-center gap-1 rounded-md py-1.5 px-2 h-auto text-[11px] transition-all duration-200 ${
              !isUsingSystem && theme === 'dark' && !isForcedDark
                ? 'bg-white dark:bg-gray-800 shadow-sm text-foreground' 
                : 'text-foreground/50 hover:text-foreground/70'
            }`}
            data-testid="theme-dark"
            title="Modo oscuro"
          >
            <Moon className="w-3.5 h-3.5" />
            <span>Oscuro</span>
          </Button>
        </div>
        
        <Button
          variant="ghost"
          onClick={handleLogout}
          className="w-full justify-start text-foreground/60 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg text-sm py-2 h-auto"
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Cerrar Sesión
        </Button>
      </div>
    </aside>
  );
};

export default Sidebar;
